export const dateFromNow = (
  date: Date,
  now?: Date
): { seconds: number; minutes: number; hours: number; days: number; months: number } => {
  const nowTime = new Date(now || Date.now()).valueOf()
  const diffTime = date.valueOf()
  const fromNowDiff = diffTime - nowTime

  return {
    months: Math.round(fromNowDiff / 1000 / 60 / 60 / 24 / 30),
    days: Math.round(fromNowDiff / 1000 / 60 / 60 / 24),
    hours: Math.round(fromNowDiff / 1000 / 60 / 60),
    minutes: Math.round(fromNowDiff / 1000 / 60),
    seconds: Math.round(fromNowDiff / 1000),
  }
}

export enum DateFromNowFormatEnum {
  SHORT,
  LONG,
}

export const formatFromNow = (
  { seconds = 0, minutes = 0, hours = 0, days = 0, months = 0 },
  format = DateFromNowFormatEnum.LONG
): string => {
  if (months >= 1) {
    return months === 1 ? `a month ago` : `${months} months ago`
  } else if (days >= 1) {
    return format === DateFromNowFormatEnum.SHORT
      ? `${days}d ago`
      : days === 1
      ? `${days} day ago`
      : `${days} days ago`
  } else if (hours >= 1) {
    return format === DateFromNowFormatEnum.SHORT
      ? `${hours}h ago`
      : hours === 1
      ? `${hours} hour ago`
      : `${hours} hours ago`
  } else if (minutes >= 1) {
    return format === DateFromNowFormatEnum.SHORT
      ? `${minutes}m ago`
      : minutes === 1
      ? `${minutes} minute ago`
      : `${minutes} minutes ago`
  } else if (seconds >= 1) {
    return format === DateFromNowFormatEnum.SHORT
      ? `${seconds}s ago`
      : seconds === 1
      ? `${seconds} second ago`
      : `${seconds} seconds ago`
  } else {
    return ''
  }
}

export const timeAgo = (date: Date, format = DateFromNowFormatEnum.LONG): string => {
  const fromNow = dateFromNow(date)
  return formatFromNow(
    {
      months: Math.abs(fromNow.months),
      days: Math.abs(fromNow.days),
      hours: Math.abs(fromNow.hours),
      minutes: Math.abs(fromNow.minutes),
      seconds: Math.abs(fromNow.seconds),
    },
    format
  )
}

export const getLastActiveStatus = (lastOnline: Date | string, format: DateFromNowFormatEnum) => {
  if (!lastOnline) {
    return ''
  }

  const lastOnlineDate = typeof lastOnline === 'string' ? new Date(lastOnline) : lastOnline
  const isUserOnlineNow = isOnlineNow(lastOnline)

  if (isUserOnlineNow) {
    return 'Active now'
  }

  const sevenDaysMs = 1000 * 60 * 60 * 24 * 7
  const aWeekAgo = new Date(new Date().getTime() - sevenDaysMs)

  if (lastOnlineDate >= aWeekAgo) {
    const fromNow = dateFromNow(lastOnlineDate)
    return `Active ${formatFromNow(
      {
        days: Math.abs(fromNow.days),
        hours: Math.abs(fromNow.hours),
        minutes: Math.abs(fromNow.minutes),
        seconds: Math.abs(fromNow.seconds),
      },
      format
    )}`
  } else {
    return `Active over a week ago`
  }
}

export const isOnlineNow = (lastOnline: Date | string): boolean => {
  if (!lastOnline) {
    return false
  }

  const MAX_TIME_DIFF = 15
  const lastOnlineTime = typeof lastOnline === 'string' ? new Date(lastOnline) : lastOnline
  const timeDiffMins = Math.abs(dateFromNow(lastOnlineTime).minutes)

  return timeDiffMins < MAX_TIME_DIFF
}
